export type HarmonicParamSemantics = "revolutionsPerBuffer" | "samplesPerRevolution";

function isPow2(n: number): boolean {
  return n > 0 && (n & (n - 1)) === 0;
}

function fftRadix2(re: Float64Array, im: Float64Array): void {
  const n = re.length;
  if (n <= 1) {
    return;
  }

  let j = 0;
  for (let i = 0; i < n - 1; i += 1) {
    if (i < j) {
      let t = re[i];
      re[i] = re[j];
      re[j] = t;
      t = im[i];
      im[i] = im[j];
      im[j] = t;
    }
    let k = n >> 1;
    while (k <= j) {
      j -= k;
      k >>= 1;
    }
    j += k;
  }

  for (let size = 2; size <= n; size <<= 1) {
    const half = size >>> 1;
    const wmCos = Math.cos((-2 * Math.PI) / size);
    const wmSin = Math.sin((-2 * Math.PI) / size);
    for (let k0 = 0; k0 < n; k0 += size) {
      let wCos = 1;
      let wSin = 0;
      for (let jLocal = k0; jLocal < k0 + half; jLocal += 1) {
        const idx = jLocal + half;
        const tr = wCos * re[idx] - wSin * im[idx];
        const ti = wCos * im[idx] + wSin * re[idx];
        re[idx] = re[jLocal] - tr;
        im[idx] = im[jLocal] - ti;
        re[jLocal] += tr;
        im[jLocal] += ti;
        const nwCos = wCos * wmCos - wSin * wmSin;
        wSin = wCos * wmSin + wSin * wmCos;
        wCos = nwCos;
      }
    }
  }
}

function ifftRadix2(re: Float64Array, im: Float64Array): void {
  const n = re.length;
  for (let i = 0; i < n; i += 1) {
    im[i] *= -1;
  }
  fftRadix2(re, im);
  const inv = 1 / n;
  for (let i = 0; i < n; i += 1) {
    im[i] *= -inv;
    re[i] *= inv;
  }
}

function applyOrbitBandpassMask(re: Float64Array, im: Float64Array, n: number, kLo: number, kHi: number): void {
  re[0] = 0;
  im[0] = 0;
  if (n % 2 === 0) {
    const nyq = n >> 1;
    re[nyq] = 0;
    im[nyq] = 0;
  }
  for (let k = 1; k < n >>> 1; k += 1) {
    if (k < kLo || k > kHi) {
      re[k] = 0;
      im[k] = 0;
      const nk = n - k;
      re[nk] = 0;
      im[nk] = 0;
    }
  }
}

function bandpassRealSequenceFft(ac: number[], n: number, kLo: number, kHi: number): number[] {
  const re = new Float64Array(n);
  const im = new Float64Array(n);
  for (let i = 0; i < n; i += 1) {
    re[i] = ac[i];
  }
  fftRadix2(re, im);
  applyOrbitBandpassMask(re, im, n, kLo, kHi);
  ifftRadix2(re, im);
  const out = new Array<number>(n);
  for (let i = 0; i < n; i += 1) {
    out[i] = re[i];
  }
  return out;
}

function revolutionsPerBufferFromDoc(n: number, resolution: number | undefined, param3: number): number {
  const res = resolution && resolution > 0 ? resolution : n;
  return (n * param3) / res;
}

export function orbitRevolutionsPerBuffer(
  bufferLength: number,
  resolution: number | undefined,
  param3: number,
  semantics: HarmonicParamSemantics,
): number {
  const n = bufferLength;
  if (semantics === "samplesPerRevolution") {
    return n / param3;
  }
  return revolutionsPerBufferFromDoc(n, resolution, param3);
}

export function harmonicDftBinIndex(
  bufferLength: number,
  resolution: number | undefined,
  param3: number,
  m: number,
  semantics: HarmonicParamSemantics,
): number | null {
  const n = bufferLength;
  if (!n || !param3 || param3 <= 0) {
    return null;
  }
  let k: number;
  if (semantics === "samplesPerRevolution") {
    k = Math.round((m * n) / param3);
  } else {
    k = Math.round(m * revolutionsPerBufferFromDoc(n, resolution, param3));
  }
  if (k <= 0 || k >= n / 2) {
    return null;
  }
  return k;
}

function singleBinEnergyAtK(xAc: number[], yAc: number[], k: number): number {
  const n = xAc.length;
  const omega = (2 * Math.PI * k) / n;
  let ax = 0;
  let bx = 0;
  let ay = 0;
  let by = 0;
  for (let t = 0; t < n; t += 1) {
    const c = Math.cos(omega * t);
    const s = Math.sin(omega * t);
    ax += xAc[t] * c;
    bx += xAc[t] * s;
    ay += yAc[t] * c;
    by += yAc[t] * s;
  }
  ax = (2 * ax) / n;
  bx = (2 * bx) / n;
  ay = (2 * ay) / n;
  by = (2 * by) / n;
  return ax * ax + bx * bx + ay * ay + by * by;
}

export function resolveOrbitHarmonicParamSemantics(
  xData: number[],
  yData: number[],
  param3: number,
  resolution?: number,
): HarmonicParamSemantics {
  const n = xData.length;
  if (!n || n !== yData.length || !param3 || param3 <= 0) {
    return "revolutionsPerBuffer";
  }

  const mx = xData.reduce((a, b) => a + b, 0) / n;
  const my = yData.reduce((a, b) => a + b, 0) / n;
  const xAc = xData.map(v => v - mx);
  const yAc = yData.map(v => v - my);

  const kRev = harmonicDftBinIndex(n, resolution, param3, 1, "revolutionsPerBuffer");
  const kSpr = harmonicDftBinIndex(n, resolution, param3, 1, "samplesPerRevolution");

  if (kRev == null && kSpr == null) {
    return "revolutionsPerBuffer";
  }
  if (kRev == null) {
    return "samplesPerRevolution";
  }
  if (kSpr == null) {
    return "revolutionsPerBuffer";
  }

  const eRev = singleBinEnergyAtK(xAc, yAc, kRev);
  const eSpr = singleBinEnergyAtK(xAc, yAc, kSpr);
  return eSpr > eRev ? "samplesPerRevolution" : "revolutionsPerBuffer";
}

export function synthesizeOrbitFromSelectedBinCenters(
  xData: number[],
  yData: number[],
  binCenters: number[],
  resolution: number | undefined,
  param3: number,
  semantics: HarmonicParamSemantics,
): { x: number[]; y: number[] } {
  const n = xData.length;
  if (!n || n !== yData.length || !binCenters.length || !param3 || param3 <= 0) {
    return { x: [...xData], y: [...yData] };
  }

  const orders = new Set<number>();
  for (const c of binCenters) {
    const k = Math.round(c);
    if (k > 0 && k < n / 2) {
      orders.add(k);
    }
  }
  if (!orders.size) {
    return { x: [...xData], y: [...yData] };
  }

  const revolutionsPerBuffer = orbitRevolutionsPerBuffer(n, resolution, param3, semantics);

  const mx = xData.reduce((a, b) => a + b, 0) / n;
  const my = yData.reduce((a, b) => a + b, 0) / n;
  const xAc = xData.map(v => v - mx);
  const yAc = yData.map(v => v - my);

  const xOut = new Array<number>(n).fill(0);
  const yOut = new Array<number>(n).fill(0);

  const halfBand = Math.max(1, Math.min(Math.round(revolutionsPerBuffer * 0.005), 2));

  const added = new Set<number>();
  for (const kCenter of [...orders].sort((a, b) => a - b)) {
    for (let offset = -halfBand; offset <= halfBand; offset += 1) {
      const k = kCenter + offset;
      if (k <= 0 || k >= n || added.has(k)) continue;
      added.add(k);

      const omega = (2 * Math.PI * k) / n;
      let Ax = 0;
      let Bx = 0;
      let Ay = 0;
      let By = 0;
      for (let t = 0; t < n; t += 1) {
        const c = Math.cos(omega * t);
        const s = Math.sin(omega * t);
        Ax += xAc[t] * c;
        Bx += xAc[t] * s;
        Ay += yAc[t] * c;
        By += yAc[t] * s;
      }
      Ax = (2 * Ax) / n;
      Bx = (2 * Bx) / n;
      Ay = (2 * Ay) / n;
      By = (2 * By) / n;

      for (let t = 0; t < n; t += 1) {
        const c = Math.cos(omega * t);
        const s = Math.sin(omega * t);
        xOut[t] += Ax * c + Bx * s;
        yOut[t] += Ay * c + By * s;
      }
    }
  }

  return { x: xOut, y: yOut };
}

export function synthesizeOrbitFromBandpassHz(
  xData: number[],
  yData: number[],
  fsHz: number,
  fLoHz: number,
  fHiHz: number,
): { x: number[]; y: number[] } {
  const n = xData.length;
  if (!n || n !== yData.length || !Number.isFinite(fsHz) || fsHz <= 0) {
    return { x: [...xData], y: [...yData] };
  }

  const fLo = Number(fLoHz);
  const fHi = Number(fHiHz);
  if (!Number.isFinite(fLo) || !Number.isFinite(fHi) || fLo <= 0 || fHi <= fLo) {
    return { x: new Array(n).fill(0), y: new Array(n).fill(0) };
  }

  const nyquist = fsHz / 2;
  const fHiClamped = Math.min(fHi, nyquist - 1e-9);
  if (fHiClamped <= fLo) {
    return { x: new Array(n).fill(0), y: new Array(n).fill(0) };
  }

  const kLo = Math.max(1, Math.ceil((fLo * n) / fsHz));
  const kHi = Math.min(Math.floor(n / 2) - 1, Math.floor((fHiClamped * n) / fsHz));
  if (kLo > kHi) {
    return { x: new Array(n).fill(0), y: new Array(n).fill(0) };
  }

  const mx = xData.reduce((a, b) => a + b, 0) / n;
  const my = yData.reduce((a, b) => a + b, 0) / n;
  const xAc = xData.map(v => v - mx);
  const yAc = yData.map(v => v - my);

  if (isPow2(n) && n >= 128) {
    const xF = bandpassRealSequenceFft(xAc, n, kLo, kHi);
    const yF = bandpassRealSequenceFft(yAc, n, kLo, kHi);
    return { x: xF, y: yF };
  }

  const xOut = new Array<number>(n).fill(0);
  const yOut = new Array<number>(n).fill(0);

  for (let k = kLo; k <= kHi; k += 1) {
    const omega = (2 * Math.PI * k) / n;
    let ax = 0;
    let bx = 0;
    let ay = 0;
    let by = 0;
    for (let t = 0; t < n; t += 1) {
      const c = Math.cos(omega * t);
      const s = Math.sin(omega * t);
      ax += xAc[t] * c;
      bx += xAc[t] * s;
      ay += yAc[t] * c;
      by += yAc[t] * s;
    }
    ax = (2 * ax) / n;
    bx = (2 * bx) / n;
    ay = (2 * ay) / n;
    by = (2 * by) / n;

    for (let t = 0; t < n; t += 1) {
      const c = Math.cos(omega * t);
      const s = Math.sin(omega * t);
      xOut[t] += ax * c + bx * s;
      yOut[t] += ay * c + by * s;
    }
  }

  return { x: xOut, y: yOut };
}

export function getOrbitDftFrequencyBoundsHz(
  bufferLength: number,
  fsHz: number,
): { fMin: number; fMax: number } | null {
  const n = bufferLength;
  if (!Number.isFinite(fsHz) || fsHz <= 0 || !Number.isFinite(n) || n < 4) {
    return null;
  }
  const kMax = Math.floor(n / 2) - 1;
  if (kMax < 1) {
    return null;
  }
  const fMin = fsHz / n;
  const fMax = (kMax * fsHz) / n;
  if (!(fMax > fMin)) {
    return null;
  }
  return { fMin, fMax };
}

export function isValidOrbitBandpassHz(fsHz: number, fLoHz: number, fHiHz: number, bufferLength?: number): boolean {
  if (!Number.isFinite(fsHz) || fsHz <= 0) {
    return false;
  }
  const fLo = Number(fLoHz);
  const fHi = Number(fHiHz);
  if (!Number.isFinite(fLo) || !Number.isFinite(fHi) || fLo <= 0 || fHi <= fLo) {
    return false;
  }
  const nyquist = fsHz / 2;
  if (fHi >= nyquist) {
    return false;
  }
  if (bufferLength != null && bufferLength >= 4) {
    const bounds = getOrbitDftFrequencyBoundsHz(bufferLength, fsHz);
    if (!bounds) {
      return false;
    }
    const tol = 1e-9 * Math.max(bounds.fMax, 1);
    if (fLo < bounds.fMin - tol || fHi > bounds.fMax + tol) {
      return false;
    }
  }
  return true;
}
